import type { ColumnDef, Row, TableState } from '@tanstack/react-table';
import type { ReactNode } from 'react';

import React from 'react';

import type { DataTableFilterField } from '../../components/DataTable/DataTable.types';
import type { DataTableSkeletonProps } from '../../components/DataTable/DataTableSkeleton';

import { DataTable } from '../../components/DataTable/DataTable';
import { DataTableSkeleton } from '../../components/DataTable/DataTableSkeleton';
import { DateRangeFilter } from './components/DateRangeFilter/DateRangeFilter';
import { AdvancedDataTableToolbar } from './components/Filters/AdvancedDataTableToolbar';
import { TasksTableToolbarActions } from './components/TasksTableToolbarActions';
import { useDataTable } from './hooks/useDataTable';

interface TableProps<TData = any> {
  title?: string;
  exportTableName?: string;
  amountOfRows?: number;
  skeletonProps?: DataTableSkeletonProps;
  rows: TData[];
  columns: ColumnDef<TData>[];
  filterFields?: DataTableFilterField<TData>[];
  pageCount?: number;
  loading?: boolean;
  initialState?: Partial<TableState>;
  showPagination?: boolean;
  enableDateRangeFilter?: boolean;
  customFilters?: ReactNode;
  syncUrlState?: boolean;
  rowContextMenu?: (row: Row<TData>) => ReactNode;
}

interface TableWrapperProps<T = any> extends TableProps<T> {
  children: React.ReactNode;
  tableData?: any;
}

const defaultSkeleton: DataTableSkeletonProps = {
  columnCount: 5,
  cellWidths: ['10rem', '40rem', '12rem', '12rem', '8rem'],
  shrinkZero: true,
  showViewOptions: false,
};

const TableWrapper = (props: TableWrapperProps) => {
  const { skeletonProps = defaultSkeleton, loading } = props;

  if (loading) return <DataTableSkeleton {...skeletonProps} />;

  return <React.Suspense fallback={<DataTableSkeleton {...skeletonProps} />}>{props.children}</React.Suspense>;
};

export const SmartTable = (props: TableProps) => {
  const {
    rows: data,
    columns: cols,
    pageCount = 1,
    filterFields,
    initialState,
    showPagination,
    amountOfRows,
    title,
    loading,
    enableDateRangeFilter,
    customFilters,
    exportTableName,
    syncUrlState,
    rowContextMenu,
  } = props;

  const columns = React.useMemo(() => cols, [cols]);

  const { table } = useDataTable({
    data,
    columns,
    pageCount,
    filterFields,
    enableAdvancedFilter: false,
    initialState: {
      columnPinning: { right: ['actions'] },
      ...initialState,
    },
    getRowId: (originalRow, index, parent) => {
      if (React.isValidElement(originalRow)) {
        return `${parent?.id}-subrow-${index}`;
      }

      return `${originalRow.id}-${index}`;
    },
    syncUrlState,
  });

  return (
    <div className="flex flex-col gap-3">
      <AdvancedDataTableToolbar
        loading={loading}
        table={table}
        filterFields={filterFields}
        title={title}
        amountOfRows={amountOfRows}
        customFilters={customFilters}
      >
        {enableDateRangeFilter && <DateRangeFilter />}
        <TasksTableToolbarActions table={table} title={exportTableName || title} />
      </AdvancedDataTableToolbar>
      <TableWrapper {...props}>
        <DataTable table={table} showPagination={showPagination} rowContextMenu={rowContextMenu} />
      </TableWrapper>
    </div>
  );
};
